/**
 * Hook: Derived state for dashboard (step configs, completion flags, canGoToDashboard).
 * Single Responsibility: compute stepConfigs, filteredWizardStatus, step1/step3 completed, canGoToDashboard.
 * Step 2 (Invite users) is only shown when canInviteUsers is true (primary-organization or organization scope).
 */

import { useMemo } from 'react';
import { STEP_CONFIGS } from '../constants';
import {
  getStepsFromOnboardingStatus,
  isOnboardingStepCompleted,
} from '../../../utils/onboardingStepsUtils';

/**
 * Pure: filter step configs by feature flag and invite permission.
 * - Exclude step 3 when cleaning standards off.
 * - Exclude step 2 (Invite users) when user cannot invite (only primary-organization and organization can).
 * @param {boolean} cleaningStandardsEnabled
 * @param {boolean} canInviteUsers
 * @returns {Array}
 */
export function filterStepConfigs(cleaningStandardsEnabled, canInviteUsers = true) {
  return STEP_CONFIGS.filter((c) => {
    if (c.stepNumber === 3 && !cleaningStandardsEnabled) return false;
    if (c.stepNumber === 2 && !canInviteUsers) return false;
    return true;
  });
}

/**
 * Pure: filter wizard status steps by feature flag and invite permission.
 * @param {Object|null} wizardStatus
 * @param {boolean} cleaningStandardsEnabled
 * @param {boolean} canInviteUsers
 * @returns {Object|null}
 */
export function filterWizardStatusForFeature(
  wizardStatus,
  cleaningStandardsEnabled,
  canInviteUsers = true,
) {
  if (!wizardStatus) return null;
  const steps = wizardStatus.steps ?? [];
  const filtered = steps.filter((s) => {
    if (s.step === 3 && !cleaningStandardsEnabled) return false;
    if (s.step === 2 && !canInviteUsers) return false;
    return true;
  });
  if (filtered.length === steps.length) return wizardStatus;
  return { ...wizardStatus, steps: filtered };
}

export function useDashboardDerivedState(
  wizardStatus,
  onboardingStatus,
  cleaningStandardsEnabled,
  canInviteUsers = true,
) {
  const stepConfigs = useMemo(
    () => filterStepConfigs(cleaningStandardsEnabled, canInviteUsers),
    [cleaningStandardsEnabled, canInviteUsers],
  );

  const filteredWizardStatus = useMemo(
    () => filterWizardStatusForFeature(wizardStatus, cleaningStandardsEnabled, canInviteUsers),
    [wizardStatus, cleaningStandardsEnabled, canInviteUsers],
  );

  const steps = getStepsFromOnboardingStatus(onboardingStatus);
  const step1Completed = isOnboardingStepCompleted(steps, 'step1');

  const step3Completed = useMemo(() => {
    if (!cleaningStandardsEnabled) return true;
    const stepsForMemo = getStepsFromOnboardingStatus(onboardingStatus);
    return isOnboardingStepCompleted(stepsForMemo, 'step3');
  }, [cleaningStandardsEnabled, onboardingStatus]);

  const canGoToDashboard = useMemo(
    () =>
      onboardingStatus?.canAccessDashboard ??
      onboardingStatus?.data?.canAccessDashboard ??
      (step1Completed && step3Completed),
    [onboardingStatus, step1Completed, step3Completed],
  );

  return {
    stepConfigs,
    filteredWizardStatus,
    step1Completed,
    step3Completed,
    canGoToDashboard,
  };
}
